import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { MatExpansionPanel } from "@angular/material/expansion";
import { Select } from "@ngxs/store";
import { BehaviorSubject, combineLatest, Observable, Subject } from "rxjs";
import { delay, distinctUntilChanged, filter, first, takeUntil } from "rxjs/operators";
import { AppState } from "src/app/app.state";
import { translateAndFormat } from "src/app/i18next";
import { ZonesService } from "src/app/services/zones.service";
import { isDefined } from "src/utils/isDefined/isDefined";

import { MatDialog } from "@angular/material/dialog";
import { Division } from "src/app/models/mir-rule.model";
import { EvirDivision } from "src/app/models/shared.model";
import { ConfirmDivisionChangeComponent } from "../confirm-division-change/confirm-division-change.component";
import { SearchInputComponent } from "../search-input/search-input.component";

@Component({
	selector: "app-division-filter-v2",
	templateUrl: "./division-filter-v2.component.html",
	styleUrls: ["./division-filter-v2.component.scss"],
	encapsulation: ViewEncapsulation.None,
})
export class DivisionFilterV2Component implements OnInit, OnDestroy, AfterViewInit, OnChanges {
	constructor(private zonesService: ZonesService, private matDialog: MatDialog) {}
	@Select(AppState.selectDivisions) divisionsFromState$: Observable<EvirDivision[]>;
	@Select(AppState.getSelectedCompanyId) selectedCompanyId$: Observable<string>;

	@Output() divisionId: EventEmitter<string> = new EventEmitter();
	@Output() enableDivFilter: EventEmitter<boolean> = new EventEmitter();

	@Input() defaultDivisionId: string = null;

	@ViewChild("panel") panel: MatExpansionPanel;
	@ViewChild("panel", { read: ElementRef }) panelRef: ElementRef;
	@ViewChild(SearchInputComponent) searchInput: SearchInputComponent;

	defaultDivisions: Division[] = [];

	divisions: Division[] = [];

	divisionId$ = this.zonesService.divisionId$;

	hasOneDivision$ = this.zonesService.hasOneDivision$;

	selectedDivision: Division = null;

	onDestroy$ = new Subject();

	title = translateAndFormat("select division", "capitalize");

	previouslySelectedDiv: Division = null;
	previouslySelectedDiv$ = this.zonesService.previouslySelectedDiv$;

	isMissingDivision: boolean;

	divisions$ = new BehaviorSubject(null);

	_boolean = Boolean;

	loading = true;

	private isUpdatingDivisions = false; // Guard to prevent recursive calls in getDivisions

	companyId: string;

	count = 0;
	ngOnInit(): void {
		this.getDivisions();
	}

	ngOnChanges(changes: SimpleChanges): void {
		this.divisions$
			.pipe(
				delay(0),
				filter(divs => divs?.length > 0),
				takeUntil(this.onDestroy$),
			)
			.subscribe(_ => {
				this.isMissingDivision = this.divisions.length === 0;

				if (changes?.defaultDivisionId?.currentValue) {
					this.defaultDivisionId = changes.defaultDivisionId.currentValue;

					this.selectedDivision = this.divisions.find(d => d.divisionId === this.defaultDivisionId);
					this.divisionId$.next(this.defaultDivisionId);
				}
			});
	}

	getDivisions() {
		if (this.isUpdatingDivisions) {
			return; // Prevent recursive calls
		}

		const divs$ = this.divisionsFromState$.pipe(filter(isDefined), takeUntil(this.onDestroy$));
		const companyId$ = this.selectedCompanyId$.pipe(takeUntil(this.onDestroy$));

		const obs = combineLatest([
			divs$,
			companyId$,
			this.divisionId$.pipe(distinctUntilChanged()),
			this.previouslySelectedDiv$,
		]);
		obs.pipe(takeUntil(this.onDestroy$)).subscribe(([divs, companyId, divisionId, prevDiv]) => {
			if (this.isUpdatingDivisions) {
				return; // Prevent recursive execution
			}

			this.isUpdatingDivisions = true;

			try {
				this.defaultDivisions = [];
				this.divisions = [];

				this.companyId = companyId;

				if (divisionId && !prevDiv) {
					const alreadySelectedDivision = divs.find(d => d?.divisionId === divisionId);
					if (alreadySelectedDivision) {
						this.selectedDivision = alreadySelectedDivision;
						this.title = alreadySelectedDivision.divisionName;
					}
				} else if (prevDiv) {
					const alreadySelectedDivision = divs.find(d => d?.divisionId === divisionId);
					if (alreadySelectedDivision && prevDiv?.divisionId !== alreadySelectedDivision?.divisionId) {
						this.selectedDivision = alreadySelectedDivision;
						this.title = alreadySelectedDivision.divisionName;
					} else {
						this.selectedDivision = prevDiv;
						this.title = this.selectedDivision.divisionName;
					}
				}

				const originalDivs: EvirDivision[] = divs;

				divs = divs
					.slice()
					.sort((a, b) => {
						return a.divisionName.toLowerCase().localeCompare(b.divisionName.toLowerCase());
					})
					.filter(div => Boolean(div.divisionName));
				divs.forEach(div => {
					if (div.divisionType !== "LEGACY_LOCATION") {
						this.defaultDivisions.push(div);
						this.divisions.push(div);
					}
				});

				// the case of if the user only has child divisions and the above code did not result in divisions

				if (!this.divisions.length) {
					// check if it might only be child division user
					const childDivs = originalDivs.filter(div => div.parentDivision);
					if (childDivs.length) {
						this.divisions = childDivs
							.map(d => ({
								divisionId: d.parentDivision.id,
								divisionName: d.parentDivision.name,
								divisionType: d.parentDivision.type,
								active: true,
							}))
							.filter(
								(d, index, array) => array.findIndex(div => div.divisionId === d.divisionId) === index,
							);
					}
				}

				// Update selected div if there is  only 1 div
				if (this.divisions.length <= 1) {
					this.divisionId$.next(this.divisions[0]?.divisionId);
					this.selectedDivision = this.divisions[0];
					this.title = this.divisions[0]?.divisionName;
				}

				this.divisions$.next(this.divisions);
				this.hasOneDivision$.next(this.divisions.length === 1);
				this.loading = false;
			} finally {
				// Reset the guard after a delay to allow proper completion
				setTimeout(() => {
					this.isUpdatingDivisions = false;
				}, 0);
			}
		});
	}

	ngAfterViewInit(): void {
		// Use a simple subscription without triggering updates to avoid circular dependency
		this.hasOneDivision$
			.pipe(
				filter(val => typeof val === "boolean"),
				distinctUntilChanged(),
				delay(0),
				takeUntil(this.onDestroy$),
			)
			.subscribe(hasOneDiv => {
				this.updateDivisionSelectionState(hasOneDiv);
			});
	}

	private updateDivisionSelectionState(hasOneDiv: boolean): void {
		if (hasOneDiv) {
			this.isMissingDivision = false;
		} else {
			if (!this.selectedDivision) {
				this.isMissingDivision = true;
			} else {
				this.isMissingDivision = false;
			}
		}
	}

	ngOnDestroy(): void {
		this.previouslySelectedDiv$.next(this.selectedDivision);
		this.onDestroy$.next();
		this.onDestroy$.complete();
	}

	focusFirstOption(container: HTMLElement): void {
		if (!container) return;

		// Focus the selected option if there is one
		const selectedCheckbox = container.querySelector<HTMLElement>(
			"mat-checkbox.mat-checkbox-checked .mat-checkbox-input, mat-checkbox.mat-mdc-checkbox-checked .mat-mdc-checkbox-input",
		);
		if (selectedCheckbox) {
			selectedCheckbox.focus();
			return;
		}

		// Fallback to the very first option
		const inputs = Array.from(
			container.querySelectorAll<HTMLElement>(".mat-checkbox-input, .mat-mdc-checkbox-input"),
		);
		if (inputs.length) {
			inputs[0].focus();
		}
	}

	onDivisionKeydown(event: KeyboardEvent, container: HTMLElement): void {
		if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
			// Only intercept these keys if focus is within the listbox container
			if (!container.contains(document.activeElement)) {
				return;
			}

			event.preventDefault();

			const inputs = Array.from(
				container.querySelectorAll<HTMLElement>(".mat-checkbox-input, .mat-mdc-checkbox-input"),
			);
			if (!inputs.length) {
				return;
			}

			const activeIndex = inputs.findIndex(el => el === document.activeElement);
			let nextIndex: number;

			if (event.key === "ArrowDown") {
				nextIndex = activeIndex < inputs.length - 1 ? activeIndex + 1 : 0;
			} else if (event.key === "ArrowUp") {
				nextIndex = activeIndex > 0 ? activeIndex - 1 : inputs.length - 1;
			} else if (event.key === "Home") {
				nextIndex = 0;
			} else if (event.key === "End") {
				nextIndex = inputs.length - 1;
			}

			inputs[nextIndex].focus();
			return;
		}

		// Prevent handling Tab manually so it moves out of the listbox container naturally
		if (event.key === "Tab") {
			return;
		}
	}

	change(div: Division, checked: boolean) {
		if (checked) {
			this.selectedDivision = div;
		} else {
			this.selectedDivision = null;
		}
		this.count++;
	}

	inputChanged(search: string) {
		search = search.toLowerCase();
		if (search) {
			this.divisions = this.defaultDivisions.filter(d => d.divisionName.toLowerCase().includes(search));
		} else {
			this.divisions = this.defaultDivisions;
		}
	}

	cancelClicked() {
		this.panel.close();
	}

	onPanelOpened(): void {
		this.searchInput?.reset();
		this.divisions = this.defaultDivisions;
	}

	onPanelClosed(): void {
		const appliedDiv = this.previouslySelectedDiv$.value;
		if (appliedDiv) {
			this.selectedDivision = appliedDiv;
		} else {
			const currentDivId = this.divisionId$.value;
			this.selectedDivision = currentDivId
				? this.divisions.find(d => d.divisionId === currentDivId) ?? null
				: null;
		}
		this.count = 0;
	}

	_selectedDivisionActions() {
		if (this.selectedDivision) {
			this.divisionId.emit(this.selectedDivision?.divisionId);
			this.zonesService.divisionId$.next(this.selectedDivision?.divisionId);

			this.title = this.selectedDivision?.divisionName;
			this.previouslySelectedDiv$.next(this.selectedDivision);

			this.panel.close();
			(this.panelRef?.nativeElement as HTMLElement)
				?.querySelector<HTMLElement>("mat-expansion-panel-header")
				?.focus();
		}
	}

	applyClicked() {
		if (this.count > 0 && !this.previouslySelectedDiv$.value) {
			this._selectedDivisionActions();
			return;
		}
		this.previouslySelectedDiv$.pipe(filter(isDefined), first()).subscribe(div => {
			if (div.divisionId === this.selectedDivision?.divisionId) {
				// no popup if the user picks the same selected division, just close panel
				this.panel.close();
				return;
			}
			// else, open the dialog for user to confirm
			const dialogRef = this.matDialog.open(ConfirmDivisionChangeComponent, {
				disableClose: false,
				autoFocus: "first-tabbable",
				restoreFocus: true,
			});

			// gets dispatched if user clicks Apply from the above opened dialog
			this.zonesService.confirmDivisionChange$.pipe(first()).subscribe(_ => {
				dialogRef
					.afterClosed()
					.pipe(first())
					.subscribe(() => {
						this._selectedDivisionActions();
						this.zonesService.selectedZonesEnd$.next([]);
						this.zonesService.selectedZonesStart$.next([]);
						this.zonesService.selectedZonesExcluded$.next([]);
					});
			});
		});
	}
}
